import { useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { secureLog } from '@/lib/security';

interface ProductionLoggerProps {
  children: React.ReactNode;
}

export const ProductionLogger = ({ children }: ProductionLoggerProps) => {
  useEffect(() => {
    const logToConsole = (level: string, message: string, data?: any) => {
      const timestamp = new Date().toISOString();
      const logMessage = `[${timestamp}] [${level}] ${message}`;
      
      if (data) {
        secureLog.info(logMessage, data);
      } else {
        secureLog.info(logMessage);
      }
    };

    // Test Supabase connectivity
    const testSupabaseConnection = async () => {
      try {
        logToConsole('INFO', 'Testing Supabase connection...');
        
        const { data, error } = await supabase
          .from('profiles')
          .select('count')
          .limit(1);
        
        if (error) {
          secureLog.error('Supabase connection failed', error);
          return false;
        }
        
        secureLog.info('Supabase connection successful');
        return true;
      } catch (err) {
        secureLog.error('Supabase connection error', err);
        return false;
      }
    };

    // Log app initialization
    logToConsole('INFO', 'App initializing...');
    logToConsole('INFO', 'Environment', {
      NODE_ENV: process.env.NODE_ENV,
      userAgent: navigator.userAgent,
      url: window.location.href,
      timestamp: new Date().toISOString()
    });

    // Test Supabase
    testSupabaseConnection();

    // Log global errors
    const handleError = (event: ErrorEvent) => {
      logToConsole('ERROR', 'Global error caught', {
        message: event.message,
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
        error: event.error
      });
    };

    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      logToConsole('ERROR', 'Unhandled promise rejection', {
        reason: event.reason
      });
    };

    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    // Periodic health check
    const healthCheckInterval = setInterval(() => {
      logToConsole('INFO', 'Health check', {
        timestamp: new Date().toISOString(),
        memoryUsage: (performance as any).memory ? {
          used: Math.round((performance as any).memory.usedJSHeapSize / 1024 / 1024),
          total: Math.round((performance as any).memory.totalJSHeapSize / 1024 / 1024)
        } : 'not available'
      });
    }, 30000); // Every 30 seconds

    return () => {
      window.removeEventListener('error', handleError);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
      clearInterval(healthCheckInterval);
    };
  }, []);

  return <>{children}</>;
};